import { type RefObject, useEffect, useRef, useState } from "react";

import {
  autoScrollForElements,
  autoScrollWindowForElements,
} from "@atlaskit/pragmatic-drag-and-drop-auto-scroll/element";
import {
  attachClosestEdge,
  type Edge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";

import { usePageScroll } from "src/components/layout/page-scroll";

/**
 * Drag and drop for the area's sortable lists. Each list names its kind of
 * item, so a dragged task never lights up a status list and the reverse.
 */

const KIND_KEY = "ampBoardSortKind";
const ITEM_KEY = "ampBoardSortId";
const ORIGIN_KEY = "ampBoardSortOrigin";
const TARGET_KEY = "ampBoardSortTargetId";

/** The group an item was picked up from at each grouping level, when it shows in more than one. */
export type SortOrigin = (string | null)[];

/** Where an item was dropped: onto a group, and before or after one of its items. */
export type SortDrop = {
  itemId: string;
  origin?: SortOrigin;
  /** The item it was dropped beside, if it landed on one. */
  besideId: string | null;
  edge: "top" | "bottom" | null;
};

function draggedId(
  kind: string,
  data: Record<string | symbol, unknown>,
): string | null {
  const value = data[ITEM_KEY];
  return data[KIND_KEY] === kind && typeof value === "string" ? value : null;
}

function dragOrigin(
  data: Record<string | symbol, unknown>,
): SortOrigin | undefined {
  const value = data[ORIGIN_KEY];
  return Array.isArray(value) ? value : undefined;
}

function dropEdge(data: Record<string | symbol, unknown>): SortDrop["edge"] {
  const edge = extractClosestEdge(data);
  return edge === "top" || edge === "bottom" ? edge : null;
}

/** Makes an element drag an item of a kind. */
export function useDraggableItem(
  ref: RefObject<HTMLElement>,
  kind: string,
  id: string,
  {
    isEnabled = true,
    origin,
    handleRef,
  }: {
    isEnabled?: boolean;
    origin?: SortOrigin;
    /** Only this part starts a drag, so the rest of the element stays editable. */
    handleRef?: RefObject<HTMLElement>;
  } = {},
) {
  const [isDragging, setIsDragging] = useState(false);
  // Read when a drag starts, so a new origin array each render doesn't re-register the element.
  const originRef = useRef(origin);
  originRef.current = origin;

  useEffect(() => {
    const element = ref.current;
    if (!element || !isEnabled) return;
    return draggable({
      element,
      dragHandle: handleRef?.current ?? undefined,
      getInitialData: () => ({
        [KIND_KEY]: kind,
        [ITEM_KEY]: id,
        [ORIGIN_KEY]: originRef.current,
      }),
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });
  }, [ref, kind, id, isEnabled, handleRef]);

  return isDragging;
}

/**
 * Makes an item a place to drop beside, and returns the edge a dragged item
 * hovers over, for drawing the insertion line. With `onDrop`, the item takes
 * the drop itself, as rows in a windowed list must, since no group element
 * contains them.
 */
export function useDropIndicator(
  ref: RefObject<HTMLElement>,
  kind: string,
  id: string,
  isEnabled: boolean,
  {
    onDrop,
    canAccept,
  }: {
    onDrop?: (drop: SortDrop) => void;
    canAccept?: (itemId: string, origin: SortOrigin | undefined) => boolean;
  } = {},
) {
  const [edge, setEdge] = useState<Edge | null>(null);
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;
  const canAcceptRef = useRef(canAccept);
  canAcceptRef.current = canAccept;

  useEffect(() => {
    const element = ref.current;
    if (!element || !isEnabled) return;
    return dropTargetForElements({
      element,
      canDrop: ({ source }) => {
        const dragged = draggedId(kind, source.data);
        return (
          dragged !== null &&
          dragged !== id &&
          (canAcceptRef.current?.(dragged, dragOrigin(source.data)) ?? true)
        );
      },
      getData: ({ input }) =>
        attachClosestEdge(
          { [TARGET_KEY]: id },
          { element, input, allowedEdges: ["top", "bottom"] },
        ),
      onDrag: ({ self }) => setEdge(extractClosestEdge(self.data)),
      onDragLeave: () => setEdge(null),
      onDrop: ({ source, self }) => {
        setEdge(null);
        const dragged = draggedId(kind, source.data);
        if (dragged && onDropRef.current) {
          onDropRef.current({
            itemId: dragged,
            origin: dragOrigin(source.data),
            besideId: id,
            edge: dropEdge(self.data),
          });
        }
      },
    });
  }, [ref, kind, id, isEnabled]);

  return edge;
}

/**
 * Makes a group a drop target. The drop reports the item beside which it
 * landed, when it landed on one, so the group can place it there.
 */
export function useGroupDropTarget(
  ref: RefObject<HTMLElement>,
  kind: string,
  onDrop: (drop: SortDrop) => void,
  isEnabled: boolean,
  canAccept?: (itemId: string, origin: SortOrigin | undefined) => boolean,
) {
  const [isOver, setIsOver] = useState(false);
  // The target registers once; the refs keep its handlers current.
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;
  const canAcceptRef = useRef(canAccept);
  canAcceptRef.current = canAccept;

  useEffect(() => {
    const element = ref.current;
    if (!element || !isEnabled) return;
    return dropTargetForElements({
      element,
      canDrop: ({ source }) => {
        const dragged = draggedId(kind, source.data);
        return (
          dragged !== null &&
          (canAcceptRef.current?.(dragged, dragOrigin(source.data)) ?? true)
        );
      },
      onDragEnter: () => setIsOver(true),
      onDragLeave: () => setIsOver(false),
      onDrop: ({ source, location }) => {
        setIsOver(false);
        const itemId = draggedId(kind, source.data);
        if (!itemId) return;
        // The innermost target is the item it landed beside, if any.
        const inner = location.current.dropTargets[0]?.data;
        const besideId =
          inner && typeof inner[TARGET_KEY] === "string"
            ? inner[TARGET_KEY]
            : null;
        onDropRef.current({
          itemId,
          origin: dragOrigin(source.data),
          besideId,
          edge: inner ? dropEdge(inner) : null,
        });
      },
    });
  }, [ref, kind, isEnabled]);

  return isOver;
}

/** Scrolls a container while an item of a kind is dragged near its edges. */
export function useSortAutoScroll(ref: RefObject<HTMLElement>, kind: string) {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    return autoScrollForElements({
      element,
      canScroll: ({ source }) => draggedId(kind, source.data) !== null,
    });
  }, [ref, kind]);
}

/** Scrolls the page, the window or the card in its place, while an item of a kind is dragged near its edges. */
export function useSortPageAutoScroll(kind: string) {
  const { isWindow, element } = usePageScroll();
  useEffect(() => {
    const canScroll = ({
      source,
    }: {
      source: { data: Record<string, unknown> };
    }) => draggedId(kind, source.data) !== null;
    if (isWindow) return autoScrollWindowForElements({ canScroll });
    if (!element) return;
    return autoScrollForElements({ element, canScroll });
  }, [kind, isWindow, element]);
}

/** The index a drop lands at in a group's items, not counting the dragged one. */
export function dropIndex(
  ids: string[],
  { itemId, besideId, edge }: SortDrop,
): number {
  const rest = ids.filter((id) => id !== itemId);
  if (!besideId) return rest.length;
  const index = rest.indexOf(besideId);
  if (index === -1) return rest.length;
  return edge === "bottom" ? index + 1 : index;
}
